/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { currentAppEntity } from 'app/developer-v3/selectors';
import { getAppUrl } from 'app/developer-v3/utils';
import * as CliApp from 'app/entities/CliApp';
import { connect } from 'react-redux';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  FAQ,
  Grid,
  Link,
  Spacer,
  Text,
} from '@zapier/design-system';
import NoZapTemplatesAlert from './NoZapTemplatesAlert';
import { AutomationBannerSection } from './AutomationBannerSection/AutomationBannerSection';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { PARTNER_SOLUTIONS_BASE_DOC_URL } from 'app/developer-v3/constants';

type Props = {
  appId: ID;
  appStatus: string;
};

const styleMedia = css`
  width: 100%;
  height: auto;
  /* Use filter to follow the actual alpha channel of the image */
  filter: drop-shadow(0 4px 12px rgb(0 0 0 / 20%));
  /* Match the baked-in border radius of one of our images */
  border-radius: 20px;
`;

const stylePage = css`
  padding: 25px 40px;
`;

const styleRowGap = css`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

function EmbedZapier({ appId, appStatus }: Props): JSX.Element {
  const appUrl = getAppUrl(appId);
  return (
    <div css={stylePage}>
      <Text margin="0px 0px 30px 0px" tag="h1" type="PageHeader">
        Embed Zapier in your app
      </Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {appStatus === 'private' && (
          <Alert title="Publish your integration to embed Zapier">
            <AlertDescription>
              Your users can discover new integrations and build automated
              workflows without ever leaving your product.{' '}
              <Link href={`${appUrl}/publishing`}>Publish</Link> your
              integration to become a partner and embed Zapier.
            </AlertDescription>
          </Alert>
        )}
        <NoZapTemplatesAlert />
      </div>
      <AutomationBannerSection />
      <Text
        margin="50px 0 50px 0"
        tag="h2"
        textAlign="center"
        type="PageHeader"
      >
        Find the Right Embed Solution
      </Text>
      <Grid columns="repeat(auto-fit, minmax(300px, 1fr))" gap="40px 30px">
        <div>
          <figure>
            <video
              controls={true}
              css={styleMedia}
              height={1080}
              loop={true}
              muted={true}
              src={`${process.env.MEDIA_URL}/videos/workflow-element.mp4`}
              width={1920}
            >
              Workflow Element
            </video>
          </figure>
        </div>
        <div data-intercom-target="WFE">
          <div>
            <Text margin="0 0 10px 0" tag="h3" type="ParagraphHeader3">
              Easily embed Zapier with pre-built tooling
            </Text>
            <Badge variant="primary">Implement in minutes</Badge>
          </div>
          <Text margin="15px 0px" tag="p" type="Body1">
            The Workflow Element is a prebuilt UI component that offers the
            quickest—and easiest—way to surface your Zapier integration directly
            within your own product. With just a few clicks, the Workflow
            Element allows your customers to try out premade zap templates,
            create automated workflows and manage zaps.
          </Text>

          {appStatus !== 'private' && (
            <Button
              color="primary"
              href={`${ZAPIER_APP_BASE_URL}/partner/embed/workflow`}
            >
              Embed the Workflow Element
            </Button>
          )}
        </div>
      </Grid>
      <Spacer height={60} />
      <Grid columns="repeat(auto-fit, minmax(300px, 1fr))" gap="40px 30px">
        <div data-intercom-target="API">
          <div>
            <Text margin="0 0 10px 0" tag="h3" type="ParagraphHeader3">
              Seamlessly integrate Zap creation into your own app
            </Text>
            <Badge variant="success">Requires software development</Badge>
          </div>
          <Text margin="15px 0px" tag="p" type="Body1">
            The Workflow API is a powerful tool for building native feeling
            integrations and workflows for your end users. It allows your app
            access to the industry’s largest integration directory that spans
            over 7,000 apps, enabling you to easily build and manage the most
            impactful workflows for your users.
          </Text>
          <Text margin="15px 0px" tag="p" type="Body1">
            Plus, our team will guide the build process with technical support
            for a smooth, tailored integration.
          </Text>
          {appStatus !== 'private' && (
            <Button
              color="primary"
              href={`${PARTNER_SOLUTIONS_BASE_DOC_URL}/workflow-api/intro`}
            >
              Check out the Workflow API
            </Button>
          )}
        </div>
        <div>
          <figure>
            <img
              alt="Workflow API"
              css={styleMedia}
              height={960}
              src={`${process.env.MEDIA_URL}/images/workflow-api/workflow-api.png`}
              width={1810}
            />
          </figure>
        </div>
      </Grid>
      <Spacer height={60} />
      <Grid columns="repeat(auto-fit, minmax(300px, 1fr))" gap="40px 30px">
        <div>
          <figure>
            <img
              alt="Embed Gallery"
              css={styleMedia}
              height={960}
              src={`${process.env.MEDIA_URL}/images/embed-zapier/gallery-picture.png`}
              width={1810}
            />
          </figure>
        </div>
        <div data-intercom-target="embed-gallery">
          <div>
            <Text margin="0 0 10px 0" tag="h3" type="ParagraphHeader3">
              See how other companies build with Zapier
            </Text>
            {/* <Badge color="info">Requires software development</Badge> */}
          </div>
          <Text margin="15px 0px" tag="p" type="Body1">
            Learn how other companies already empower their users with
            effortless app integration and automation capabilities. Whether you
            want to quickly embed pre-made Zap templates or create fully
            customized experiences, our embed tools make it simple for your
            customers to connect your apps with all their other go-to tools and
            services.
          </Text>
          {appStatus !== 'private' && (
            <div css={styleRowGap}>
              <Button
                color="primary"
                href={`${ZAPIER_APP_BASE_URL}/developer-platform/partner-embeds`}
              >
                Browse the gallery
              </Button>
              <Button
                color="secondary"
                href="https://asset.cloudinary.com/zapier-media/e784d88c297dae13d40b88424bc01774"
              >
                Download case studies
              </Button>
            </div>
          )}
        </div>
      </Grid>
      <Spacer height={20} />
      <FAQ
        items={[
          {
            title: `How much does it cost to implement Zapier?`,
            content: <p>Nothing&mdash;it's free for all our partners!</p>,
          },
          {
            title: `How do I get started?`,
            content: (
              <p>
                You can easily generate the code for our plug-and-play solutions
                by visiting our{' '}
                <Link
                  href="https://zapier.com/partner/embed/workflow"
                  target="_blank"
                >
                  generator
                </Link>
                . Get access to the Workflow API by requesting your API key.
                Note that you need to first build a Zapier integration to start
                using these solutions.
              </p>
            ),
          },
          {
            title: `Will we have developer support as we implement Zapier?`,
            content: (
              <p>
                We're here to make sure everything launches smoothly. We have
                fully staffed Integrations and Partner teams to guide you in the
                build process. Go to our{' '}
                <Link
                  href="https://developer.zapier.com/contact?utm_source=zapier_marketing_website&utm_medium=embed_experience&utm_campaign=solutions_marketing_page"
                  target="_blank"
                >
                  platform help center
                </Link>{' '}
                for more help.
              </p>
            ),
          },
          {
            title: `What API endpoints are available?`,
            content: (
              <p>
                Go to our{' '}
                <Link
                  href={`${PARTNER_SOLUTIONS_BASE_DOC_URL}/workflow-api/intro}?utm_source=zapier_marketing_website&utm_medium=embed_experience&utm_campaign=solutions_marketing_page`}
                  target="_blank"
                >
                  Workflow API documentation
                </Link>{' '}
                to check out our suite of APIs and endpoint documentation. We'll
                be releasing new APIs on a regular basis.
              </p>
            ),
          },
          {
            title: `How long does it take to embed Zapier?`,
            content: (
              <p>
                Our plug-and-play solutions can be embedded within minutes, and
                implementing our APIs typically takes a few weeks.
              </p>
            ),
          },
          {
            title: `Where should I implement Zapier inside my product?`,
            content: (
              <p>
                Account settings or integration settings are a popular location,
                but it depends on your product. For example, some CRM partners
                put Zapier into their contacts section, since their main use
                case is to use Zapier to automatically pull contacts into their
                CRM.
              </p>
            ),
          },
          {
            title: `Do my users need to have a Zapier account?`,
            content: (
              <p>
                Yes! They'll be guided through the account creation process when
                they interact with any of our embedded solutions.
              </p>
            ),
          },
        ]}
      />
      <Text tag="p" textAlign="center">
        Have more questions? For more help, go to our{' '}
        <Link href="https://developer.zapier.com/contact" target="_blank">
          platform help center
        </Link>
        .
      </Text>
      <Spacer height={20} />
    </div>
  );
}

function mapStateToProps(state: any, props: any): Partial<Props> {
  const app = currentAppEntity(state);
  const appStatus = CliApp.selectors.status(app);
  const appId = CliApp.selectors.id(app) || props.params.id;
  return { appId, appStatus };
}

function mapDispatchToProps(_dispatch): Partial<Props> {
  return {};
}

export default connect(mapStateToProps, mapDispatchToProps)(EmbedZapier);
